/**
 * Вода хозяйства и контура по поливам цикла (agroai-6al.37): раскладка ответа
 * `/farms/{id}/water?cycle=` и `/contours/{id}/water?cycle=`. Состояние
 * контура в поливе считает СЕРВЕР (`serving/farm_water.py`); здесь только
 * слова, тоны слоя карты и суммы для KPI — из тех же строк, что красят карту.
 *
 * Вода — события файла ирригации (`source === 'events'`, agroai-0e8): полито /
 * ждём / без подачи, «запрошено» нет. Запасная ветка — цепочка заявок, когда
 * событий в срезе нет: там главное различение — «запросили, не дали»
 * (`requested_not_delivered`: у контура есть заявка без единой подачи) против
 * «заявки нет» (`no_request`: окно закрылось, ни подачи, ни ожидающей заявки).
 */

import type { Schemas } from '../../../api/client';
import { defineMessages, type PlainMessage } from '../../../i18n/define';
import { tr } from '../../../i18n/runtime';
import type { DataTone, ValuesLayer } from '../../map/layers';
import type { ChannelRow } from '../waterSummary';
import { wateringState, type WateringState } from '../../data-source/api/water';

export type FarmWater = Schemas['FarmWaterOut'];
export type Watering = Schemas['WateringOut'];
export type WateringContour = Schemas['WateringContourOut'];

export type ContourWaterState =
  | 'delivered'
  | 'partial'
  | 'unmeasured'
  | 'requested_not_delivered'
  | 'no_request'
  | 'no_delivery'
  | 'in_window'
  | 'upcoming';

/** Порядок легенды: от поданного к требующему внимания, затем «ещё не срок». */
export const WATER_STATE_ORDER: ContourWaterState[] = [
  'delivered',
  'partial',
  'no_delivery',
  'requested_not_delivered',
  'no_request',
  'unmeasured',
  'in_window',
  'upcoming',
];

export interface WaterStateMeta {
  label: string;
  note: string;
  tone: DataTone;
  /** Токен образца легенды страницы — тот же смысл, что тон слоя. */
  swatch: string;
}

const W = defineMessages({
  deliveredLabel: { id: 'contour.water.state.delivered.label', defaultMessage: 'Подано', description: 'Состояние полива контура: воды подали достаточно; подпись легенды карты и фильтра-чипа, коротко' },
  deliveredNote: { id: 'contour.water.state.delivered.note', defaultMessage: 'не меньше 95 % запрошенного', description: 'Пояснение к состоянию «Подано» в легенде полива (менеджер, хоким); 95 % — порог' },
  partialLabel: { id: 'contour.water.state.partial.label', defaultMessage: 'Подано частично', description: 'Состояние полива контура: подали, но меньше порога; подпись легенды и чипа, коротко' },
  partialNote: { id: 'contour.water.state.partial.note', defaultMessage: 'меньше 95 % запрошенного', description: 'Пояснение к состоянию «Подано частично» в легенде полива; 95 % — порог' },
  rndLabel: { id: 'contour.water.state.requestedNotDelivered.label', defaultMessage: 'Запросили — не дали', description: 'Состояние полива контура: по заявке воды не подали ни разу; подпись легенды и чипа, коротко' },
  rndNote: { id: 'contour.water.state.requestedNotDelivered.note', defaultMessage: 'заявка есть, подачи нет', description: 'Пояснение к состоянию «Запросили — не дали» в легенде полива' },
  noRequestLabel: { id: 'contour.water.state.noRequest.label', defaultMessage: 'Заявки нет', description: 'Состояние полива контура: окно закрылось без заявки и без подачи; подпись легенды и чипа, коротко' },
  noRequestNote: { id: 'contour.water.state.noRequest.note', defaultMessage: 'окно закрылось: ни заявки, ни подачи', description: 'Пояснение к состоянию «Заявки нет» в легенде полива' },
  unmeasuredLabel: { id: 'contour.water.state.unmeasured.label', defaultMessage: 'Не измерено', description: 'Состояние полива контура: объём подачи или заявки не назван; подпись легенды и чипа, коротко' },
  unmeasuredNote: { id: 'contour.water.state.unmeasured.note', defaultMessage: 'объём подачи или заявки не назван', description: 'Пояснение к состоянию «Не измерено» в легенде полива' },
  inWindowLabel: { id: 'contour.water.state.inWindow.label', defaultMessage: 'Ждём подачи', description: 'Состояние полива контура: окно полива открыто, события в файле ирригации ещё нет; подпись легенды и чипа, коротко' },
  inWindowNote: { id: 'contour.water.state.inWindow.note', defaultMessage: 'окно идёт, события ещё нет', description: 'Пояснение к состоянию «Ждём подачи» в легенде полива' },
  upcomingLabel: { id: 'contour.water.state.upcoming.label', defaultMessage: 'Окно впереди', description: 'Состояние полива контура: окно полива ещё не открылось; подпись легенды и чипа, коротко' },
  upcomingNote: { id: 'contour.water.state.upcoming.note', defaultMessage: 'полив ещё не начался', description: 'Пояснение к состоянию «Окно впереди» в легенде полива' },
  noDeliveryLabel: { id: 'contour.water.state.noDelivery.label', defaultMessage: 'Без подачи', description: 'Состояние полива контура: окно закрылось, события в файле ирригации нет; подпись легенды и чипа, коротко' },
  noDeliveryNote: { id: 'contour.water.state.noDelivery.note', defaultMessage: 'окно закрылось: события в файле нет', description: 'Пояснение к состоянию «Без подачи» в легенде полива; файл мог опоздать' },
  evDeliveredNote: { id: 'contour.water.state.ev.delivered.note', defaultMessage: 'события файла закрыли все поливы контура', description: 'Пояснение к состоянию «Подано» в легенде полива, когда вода — события файла ирригации' },
  evPartialNote: { id: 'contour.water.state.ev.partial.note', defaultMessage: 'закрыта только часть поливов контура', description: 'Пояснение к состоянию «Подано частично» в легенде полива, когда вода — события файла ирригации' },
  noneLabel: { id: 'contour.water.layer.none', defaultMessage: 'полива на контуре нет', description: 'Подпись на карте для контуров, у которых нет полива в выбранном окне (легенда слоя «Полив»)' },
});

const STATE_SPEC: Record<ContourWaterState, { label: PlainMessage; note: PlainMessage; tone: DataTone; swatch: string }> = {
  delivered: { label: W.deliveredLabel, note: W.deliveredNote, tone: 'water', swatch: '--aqua' },
  partial: { label: W.partialLabel, note: W.partialNote, tone: 'water-soft', swatch: '--aqua-soft' },
  requested_not_delivered: { label: W.rndLabel, note: W.rndNote, tone: 'warning', swatch: '--st-warn' },
  no_delivery: { label: W.noDeliveryLabel, note: W.noDeliveryNote, tone: 'missing', swatch: '--st-missing' },
  no_request: { label: W.noRequestLabel, note: W.noRequestNote, tone: 'missing', swatch: '--st-missing' },
  unmeasured: { label: W.unmeasuredLabel, note: W.unmeasuredNote, tone: 'neutral', swatch: '--line-strong' },
  in_window: { label: W.inWindowLabel, note: W.inWindowNote, tone: 'progress', swatch: '--st-progress' },
  upcoming: { label: W.upcomingLabel, note: W.upcomingNote, tone: 'future', swatch: '--st-future' },
};

/** Подпись, пояснение и тон состояния на языке интерфейса; зовётся при рендере. */
export function waterStateMeta(state: ContourWaterState, events = false): WaterStateMeta {
  const spec = STATE_SPEC[state];
  // «95 % запрошенного» — правило заявок; у событий файла заявок нет.
  const note = events && state === 'delivered' ? W.evDeliveredNote : events && state === 'partial' ? W.evPartialNote : spec.note;
  return { label: tr(spec.label), note: tr(note), tone: spec.tone, swatch: spec.swatch };
}

const KNOWN = new Set<string>(WATER_STATE_ORDER);

export function stateOf(row: Pick<WateringContour, 'state'>): ContourWaterState | null {
  return KNOWN.has(row.state) ? (row.state as ContourWaterState) : null;
}

export const vol = (v: string | number | null | undefined): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Слой карты полива: состояние каждого контура — тоном семантической шкалы. */
export function wateringLayer(rows: ReadonlyArray<WateringContour>, title: string, events = false): ValuesLayer {
  const byId: Record<string, string> = {};
  const seen = new Set<ContourWaterState>();
  for (const r of rows) {
    const s = stateOf(r);
    if (!s) continue;
    byId[String(r.contour_id)] = s;
    seen.add(s);
  }
  return {
    title,
    byId,
    legend: WATER_STATE_ORDER.filter((s) => seen.has(s)).map((s) => ({
      key: s,
      label: waterStateMeta(s, events).label,
      tone: waterStateMeta(s, events).tone,
      note: waterStateMeta(s, events).note,
    })),
    noneLabel: tr(W.noneLabel),
  };
}

/** Число контуров по состояниям — поле ответа сервера (`state_counts`), без счёта в браузере. */
export function stateCounts(w: Pick<Watering, 'state_counts'>): Record<ContourWaterState, number> {
  const out = Object.fromEntries(WATER_STATE_ORDER.map((s) => [s, 0])) as Record<ContourWaterState, number>;
  for (const [state, count] of Object.entries(w.state_counts ?? {})) {
    if (KNOWN.has(state)) out[state as ContourWaterState] = count;
  }
  return out;
}

/** Вода кабинета читается из событий файла ирригации (иначе — запасная цепочка заявок). */
export const isEvents = (data: Pick<FarmWater, 'source'> | null | undefined): boolean => data?.source === 'events';

export function wateringPhase(w: Pick<Watering, 'window_from' | 'window_to'>, day: string): WateringState {
  return wateringState({ windowFrom: w.window_from, windowTo: w.window_to }, day);
}

export function wateringKey(w: Pick<Watering, 'campaign_id' | 'work_def_id' | 'actor'>): string {
  return `${w.campaign_id}:${w.work_def_id}:${w.actor.id}`;
}

/**
 * Полив по умолчанию: тот, чьё окно идёт на дату; иначе последний
 * закрывшийся; иначе первый. Ключ из адреса побеждает, если он есть.
 */
export function pickWatering(list: ReadonlyArray<Watering>, asked: string | null, day: string): Watering | null {
  if (list.length === 0) return null;
  const hit = asked ? list.find((w) => wateringKey(w) === asked) : undefined;
  if (hit) return hit;
  const active = list.find((w) => wateringPhase(w, day) === 'active');
  if (active) return active;
  const done = list.filter((w) => wateringPhase(w, day) === 'done');
  return done[done.length - 1] ?? list[0]!;
}

export interface WaterTotals {
  requestedM3: number | null;
  deliveredM3: number | null;
  coveragePct: number | null;
  /** Недоподача — сумма по контурам: перерасход одного не гасит недоподачу другого. */
  shortM3: number | null;
  /** Контуров-поливов «запросили — не дали» и «заявки нет» (по всем поливам). */
  requestedNotDelivered: number;
  noRequest: number;
  closedWindows: number;
  /** Ветка событий файла: контуров-поливов без подачи, событий, политая земля цикла. */
  noDelivery: number;
  events: number;
  areaHa: number | null;
}

/** Итоги цикла — поле `totals` ответа сервера (С4); при его отсутствии — «нет данных», не ноли. */
export function waterTotals(data: Pick<FarmWater, 'totals'> | null | undefined): WaterTotals {
  const t = data?.totals;
  return {
    requestedM3: vol(t?.requested_m3),
    deliveredM3: vol(t?.delivered_m3),
    coveragePct: t?.coverage_pct ?? null,
    shortM3: vol(t?.short_m3),
    requestedNotDelivered: t?.requested_not_delivered ?? 0,
    noRequest: t?.no_request ?? 0,
    closedWindows: t?.closed_windows ?? 0,
    noDelivery: t?.no_delivery ?? 0,
    events: t?.events ?? 0,
    areaHa: vol(t?.area_ha),
  };
}

/** Каналы полива в форме списка каналов экрана «Вода» — порядок и покрытие с сервера. */
export function channelRowsOf(w: Watering): ChannelRow[] {
  return w.channels.map((ch) => ({
    key: ch.label,
    label: ch.label,
    requestedM3: vol(ch.requested_m3),
    deliveredM3: vol(ch.delivered_m3),
    coveragePct: ch.coverage_pct ?? null,
    contourIds: (ch.contour_ids ?? []).map(String),
  }));
}
